import * as ex from 'excalibur';
import { ActionType, Allegiance } from '../types/enums';
import { Action, GridPosition } from '../types/interfaces';
import { ANIMATION_DURATION, TILE_SIZE } from '../config/constants';
import { Grid } from './grid';

// Base Actor class for all grid entities
export class Actor extends ex.Actor {
  public gridX: number;
  public gridY: number;
  public initiative: number;
  public actionPoints: number = 1;
  public allegiance: Allegiance;
  public health: number = 100;
  public maxHealth: number = 100;
  protected grid: Grid;

  constructor(options: {
    pos: ex.Vector,
    gridX: number,
    gridY: number,
    width: number,
    height: number,
    color: ex.Color,
    initiative: number,
    allegiance: Allegiance,
    health?: number,
    maxHealth?: number,
    grid: Grid
  }) {
    super({
      pos: options.pos,
      width: options.width,
      height: options.height,
      color: options.color
    });

    // Create a colored background to indicate allegiance
    const bgColor = options.allegiance === Allegiance.Player ?
      ex.Color.fromHex('#AADDFF') : // Light blue for player
      ex.Color.fromHex('#FFAAAA'); // Light red for enemy

    // Add a border around the colored square
    const borderColor = options.allegiance === Allegiance.Player ?
      ex.Color.Blue : ex.Color.Red;

    // Create a square background with the allegiance color and border
    const square = new ex.Rectangle({
      width: options.width - 2, // Slightly smaller to show border
      height: options.height - 2,
      color: bgColor,
      strokeColor: borderColor,
      lineWidth: 2
    });

    // Use the colored square as the base graphic
    this.graphics.use(square);

    // The emoji text will be added on top by the setEmoji method

    this.gridX = options.gridX;
    this.gridY = options.gridY;
    this.initiative = options.initiative;
    this.allegiance = options.allegiance;
    this.grid = options.grid;

    if (options.health !== undefined) this.health = options.health;
    if (options.maxHealth !== undefined) this.maxHealth = options.maxHealth;
  }

  // Move actor to a new grid position with spring animation
  public moveToGrid(gridX: number, gridY: number) {
    // Update grid reference
    this.grid.moveActor(this, this.gridX, this.gridY, gridX, gridY);

    // Update actor properties
    this.gridX = gridX;
    this.gridY = gridY;

    // Target position
    const targetX = gridX * TILE_SIZE + TILE_SIZE / 2;
    const targetY = gridY * TILE_SIZE + TILE_SIZE / 2;

    // Use spring animation
    this.springMoveTo(targetX, targetY);
  }

  // Simple elastic animation
  protected springMoveTo(targetX: number, targetY: number) {
    // Clear any ongoing actions
    this.actions.clearActions();

    // Use built-in Excalibur easing
    this.actions.easeTo(
      targetX,
      targetY,
      ANIMATION_DURATION,
      // Use built-in Excalibur elastic ease-out
      ex.EasingFunctions.EaseOutElastic
    );
  }

  // Reset action points at the start of a new turn
  public resetActionPoints() {
    this.actionPoints = 1;
  }

  // Determine next action - override in subclasses
  public getNextAction(): Action | null {
    return {
      type: ActionType.Wait,
      actor: this,
      execute: () => {
        console.log(`${this.constructor.name} waits`);
        this.actionPoints--;
      }
    };
  }
}